/**
 * Created: 2026-10-08
 * Initial commit: Show coaching appointment dialog from admin slot grid (ef978969e)
 *
 * Read-only dialog showing a coaching appointment (course, coach, booked
 * court-hours, and students) when an admin clicks a coaching slot.
 * Updated: 2026-10-21 — The appointment's coach or creator gets 修改学员 and
 * 取消预约 until the last slot has ended, as on the mini appointment page; the
 * creator is shown right of the coach.
 * Updated: 2026-10-21 — A super_admin of the current site gets 取消预约 too.
 * Updated: 2026-10-24 — A confirmed appointment whose last slot has ended
 * shows 已结束, matching the sessions list.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { dateFormat, message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { SPORT_LABEL } from '@tc-app/shared/labels'
import { useState } from 'react'
import {
  AdminCoachingAppointmentDocument,
  CancelCoachingAppointmentDocument,
  CoachingAttendeeStatus,
  CoachingSessionStatus,
  RosterMode,
  SiteRole,
} from '../gql/graphql'
import { useCurrentUser, useRole } from '../hooks'
import { getWeekdayText } from '../lib/dateUtils'
import EditRosterDialog from './EditRosterDialog'
import UserAvatar from './UserAvatar'

gql`
  query AdminCoachingAppointment($sessionId: UUID!) {
    coachingAppointment(sessionId: $sessionId) {
      __typename
      ... on CoachingAppointment {
        sessionId
        rosterMode
        status
        course {
          id
          name
          sport
          seats
        }
        coach {
          id
          openid
          name
          avatar
          mobile
          gender
        }
        creator {
          id
          openid
          name
          avatar
          mobile
          gender
        }
        slots {
          date
          hour
          court {
            id
            name
          }
        }
        attendees {
          status
          confirmTime
          headcount
          student {
            id
            openid
            name
            avatar
            mobile
            gender
          }
        }
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }

  mutation CancelCoachingAppointment($sessionId: UUID!) {
    cancelCoachingAppointment(sessionId: $sessionId) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

const APPOINTMENT_STATUS_CHIP: Record<
  CoachingSessionStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'info' }
> = {
  [CoachingSessionStatus.Scheduled]: { label: '待确认', color: 'warning' },
  [CoachingSessionStatus.Confirmed]: { label: '已确认', color: 'success' },
  [CoachingSessionStatus.Cancelled]: { label: '已取消', color: 'default' },
}

/** A confirmed appointment whose last slot has ended, as on the sessions list. */
const FINISHED_CHIP = { label: '已结束', color: 'info' } as const

const ATTENDEE_STATUS_CHIP: Record<
  CoachingAttendeeStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  [CoachingAttendeeStatus.Scheduled]: { label: '待确认', color: 'warning' },
  [CoachingAttendeeStatus.Confirmed]: { label: '已确认', color: 'success' },
  [CoachingAttendeeStatus.Cancelled]: { label: '已取消', color: 'default' },
}

const ROSTER_MODE_LABEL: Record<RosterMode, string> = {
  [RosterMode.Invite]: '邀请',
  [RosterMode.Enroll]: '报名',
}

const formatHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`

interface AppointmentDialogProps {
  sessionId: string | null
  onClose: () => void
  /** The appointment was cancelled or its roster edited. */
  onChanged?: () => void
}

export default function AppointmentDialog({
  sessionId,
  onClose,
  onChanged,
}: AppointmentDialogProps) {
  const [currentUser] = useCurrentUser()
  const { currentSite } = useRole()
  const [editingRoster, setEditingRoster] = useState(false)
  const { data, loading, error, refetch } = useQuery(AdminCoachingAppointmentDocument, {
    variables: { sessionId: sessionId ?? '' },
    skip: !sessionId,
    fetchPolicy: 'network-only',
  })

  const [cancelCoachingAppointment, { loading: cancelling }] = useMutation(
    CancelCoachingAppointmentDocument,
  )

  const result = data?.coachingAppointment
  const appointment = result?.__typename === 'CoachingAppointment' ? result : undefined
  const requestError = result?.__typename === 'GraphqlRequestError' ? result : undefined

  // Group court-hours by date: "MM-dd 周X  HH:00-HH:00/Nh  court names"
  const slotRows = appointment
    ? [...new Set(appointment.slots.map((s) => s.date))].map((date) => {
        const daySlots = appointment.slots.filter((s) => s.date === date)
        const hours = daySlots.map((s) => s.hour)
        const start = Math.min(...hours)
        const end = Math.max(...hours) + 1
        return {
          date,
          time: `${formatHour(start)}-${formatHour(end)}/${end - start}h`,
          courts: [...new Set(daySlots.map((s) => s.court.name))].join('、'),
        }
      })
    : []

  const activeCount = appointment
    ? appointment.attendees.filter((a) => a.status !== CoachingAttendeeStatus.Cancelled)
        .length
    : 0

  // The roster can be edited, and the appointment cancelled, by its coach or
  // its creator until the last slot has ended, as on the mini page.
  const lastSlot = appointment
    ? [...appointment.slots].sort(
        (a, b) => b.date.localeCompare(a.date) || b.hour - a.hour,
      )[0]
    : undefined
  const hasFinished = lastSlot
    ? Date.now() >=
      new Date(
        `${lastSlot.date}T${String(lastSlot.hour).padStart(2, '0')}:00:00+08:00`,
      ).getTime() +
        60 * 60 * 1000
    : false
  const isCoachOrCreator =
    !!appointment &&
    !!currentUser?.id &&
    (currentUser.id === appointment.coach.id ||
      currentUser.id === appointment.creator?.id)
  const isOpen =
    !!appointment &&
    appointment.status !== CoachingSessionStatus.Cancelled &&
    !hasFinished
  // The dialog only opens on appointments of the current site, so its
  // super_admin may cancel them too, as the backend allows.
  const isSuperAdmin = currentSite?.roles.includes(SiteRole.SuperAdmin) ?? false
  const canCancel = isOpen && (isCoachOrCreator || isSuperAdmin)
  const canModify =
    isOpen && isCoachOrCreator && appointment.rosterMode === RosterMode.Invite
  const statusChip = appointment
    ? appointment.status === CoachingSessionStatus.Confirmed && hasFinished
      ? FINISHED_CHIP
      : APPOINTMENT_STATUS_CHIP[appointment.status]
    : undefined

  const handleCancel = () => {
    if (!sessionId) return
    message.prompt('取消后将释放预约时段，已确认的学员课时将退回。确定要取消吗？', {
      title: '确认取消',
      noLabel: '返回',
      yesLabel: '确认取消',
      onYesButton: async () => {
        try {
          const res = (await cancelCoachingAppointment({ variables: { sessionId } }))
            .data?.cancelCoachingAppointment
          if (res?.__typename === 'GraphqlRequestError') {
            message.error(res.message || '取消失败')
          } else {
            message.success('已取消')
            onChanged?.()
          }
        } catch (e) {
          console.error('Failed to cancel appointment:', e)
          message.error('取消失败')
        }
        await refetch()
      },
    })
  }

  return (
    <Dialog open={!!sessionId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        课程预约
        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {statusChip && (
            <Chip label={statusChip.label} color={statusChip.color} size="small" />
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          <IconButton onClick={() => refetch()} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small" aria-label="关闭">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 1.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error.message}</Alert>}
        {requestError && (
          <Alert severity="error">{requestError.message ?? requestError.code}</Alert>
        )}

        {appointment && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  课程信息
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {appointment.course.name}
                  </Typography>
                  <Chip label={SPORT_LABEL[appointment.course.sport]} size="small" />
                  <Chip
                    label={ROSTER_MODE_LABEL[appointment.rosterMode]}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box
                  sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      教练
                    </Typography>
                    <UserAvatar user={appointment.coach} />
                  </Box>
                  {appointment.creator && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        创建人
                      </Typography>
                      <UserAvatar user={appointment.creator} />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  上课时间
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>日期</TableCell>
                      <TableCell>时间</TableCell>
                      <TableCell>场地</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slotRows.map((row) => (
                      <TableRow key={row.date}>
                        <TableCell>
                          {row.date} {getWeekdayText(row.date)}
                        </TableCell>
                        <TableCell>{row.time}</TableCell>
                        <TableCell>{row.courts}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  学员 ({activeCount}/{appointment.course.seats})
                </Typography>
                {appointment.attendees.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    暂无学员
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {appointment.attendees.map((a) => (
                        <TableRow key={a.student.openid}>
                          <TableCell>
                            <UserAvatar user={a.student} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {a.headcount}人
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                label={ATTENDEE_STATUS_CHIP[a.status].label}
                                color={ATTENDEE_STATUS_CHIP[a.status].color}
                                size="small"
                              />
                              {a.status === CoachingAttendeeStatus.Confirmed &&
                                a.confirmTime && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    {dateFormat(a.confirmTime, {
                                      format: 'yyyy-MM-dd HH:mm',
                                    })}
                                  </Typography>
                                )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      {(canCancel || canModify) && (
        <DialogActions>
          {canModify && (
            <Button variant="outlined" onClick={() => setEditingRoster(true)}>
              修改学员
            </Button>
          )}
          {canCancel && (
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              disabled={cancelling}
            >
              取消预约
            </Button>
          )}
        </DialogActions>
      )}
      {editingRoster && appointment && sessionId && lastSlot && (
        <EditRosterDialog
          sessionId={sessionId}
          courseId={appointment.course.id}
          seats={appointment.course.seats}
          date={lastSlot.date}
          sessionHours={appointment.slots.length}
          attendees={appointment.attendees}
          onClose={() => setEditingRoster(false)}
          onSuccess={() => {
            setEditingRoster(false)
            refetch()
            onChanged?.()
          }}
        />
      )}
    </Dialog>
  )
}
